/**
 * What a phone's answers mean, in words and in the colour they deserve — shared by the map's
 * location line and the technician's device card, so both say the same thing.
 */

export interface DeviceWord {
    label: string
    note: string
    /** A Metronic colour name: success, warning, danger, gray-600. */
    tone: string
}

export const locationWords: Record<string, DeviceWord> = {
    always: { label: "Always", note: "Followed while on the clock, even with the app closed.", tone: "success" },
    whileUsing: { label: "While Using", note: "Followed only while the app is open on their phone.", tone: "warning" },
    denied: { label: "Off", note: "Their location is not shared at all.", tone: "danger" },
    notAsked: { label: "Not asked yet", note: "The app has not asked for location on their phone yet.", tone: "gray-600" }
}

export const notificationWords: Record<string, DeviceWord> = {
    granted: { label: "Allowed", note: "New jobs, messages and reminders reach their phone.", tone: "success" },
    denied: { label: "Off", note: "Nothing the app sends reaches their phone — new jobs included.", tone: "danger" },
    notAsked: { label: "Not asked yet", note: "The app has not asked to send notifications yet.", tone: "gray-600" }
}

export const tapToPayWords: Record<string, DeviceWord> = {
    ready: { label: "Set up", note: "Ready to take contactless cards on their phone.", tone: "success" },
    notSetUp: { label: "Not set up", note: "Available, but not set up on their phone yet.", tone: "warning" },
    unsupported: { label: "Phone not supported", note: "Their phone cannot read contactless cards.", tone: "gray-600" },
    notOffered: { label: "Not offered", note: "The account does not take card payments on the phone.", tone: "gray-600" },
    failed: { label: "Failed", note: "Setting it up on their phone did not work.", tone: "danger" }
}

/** How the app is running on their phone, as it last said. */
export const appStateWords: Record<string, DeviceWord> = {
    foreground: { label: "App is open", note: "The Rinvox app is open on their screen.", tone: "success" },
    background: { label: "App is minimized", note: "The app is in the background or the phone is locked; it keeps reporting.", tone: "primary" },
    closed: { label: "App is closed", note: "They closed the app. It comes back when they open it or move about 150 m.", tone: "danger" }
}

export const platformName = (platform?: string): string | undefined => {
    const p = (platform ?? "").toLowerCase()
    if (!p) return undefined
    if (p.includes("android")) return "Android"
    if (p.includes("ios") || p.includes("iphone")) return "iPhone"
    return platform
}

/** "2026-10-16T09:12:00" read as UTC, the way the hub sends it. */
export const reportedAt = (value?: string): Date | undefined => {
    if (!value) return undefined
    const date = new Date(/Z|[+-]\d\d:?\d\d$/.test(value) ? value : value + "Z")
    return isNaN(date.getTime()) ? undefined : date
}
